import React, { useState } from 'react';
import { ArrowRight, CircleCheck, CircleX, Eye, EyeOff, LoaderCircle, Lock, Mail, ShieldCheck, User } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthProvider.jsx';
import { useNotification } from '../../components/Notifications/NotificationContext';
import AuthShell from './AuthShell.jsx';

const CRITERIA = [
  { key: 'length', label: '8+ caracteres', test: (p) => p.length >= 8 },
  { key: 'upper', label: 'Letra maiúscula', test: (p) => /[A-Z]/.test(p) },
  { key: 'lower', label: 'Letra minúscula', test: (p) => /[a-z]/.test(p) },
  { key: 'number', label: 'Número', test: (p) => /[0-9]/.test(p) },
  { key: 'special', label: 'Caractere especial', test: (p) => /[!@#$%^&*()_+\-=\[\]{};:'"",.<>?/\\|`~]/.test(p) },
];

const STRENGTH_LEVELS = [
  { label: 'Muito fraca', color: '#f87171' },
  { label: 'Fraca', color: '#f87171' },
  { label: 'Razoável', color: '#fbbf24' },
  { label: 'Boa', color: '#a3e635' },
  { label: 'Forte', color: '#10b981' },
];

function Register() {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  const { showError, showSuccess } = useNotification();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const { register } = useAuth();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const passed = CRITERIA.filter((c) => c.test(formData.password));
  const missing = CRITERIA.filter((c) => !c.test(formData.password));
  const score = passed.length;
  const strength = STRENGTH_LEVELS[Math.max(score - 1, 0)];
  const passwordsMatch =
    formData.confirmPassword.length > 0 &&
    formData.password === formData.confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validações client-side
    if (formData.password !== formData.confirmPassword) {
      showError('As senhas não coincidem');
      return;
    }

    if (score < CRITERIA.length) {
      showError('Senha deve ter 8+ caracteres, maiúscula, minúscula, número e caractere especial');
      return;
    }

    setLoading(true);
    try {
      const data = await register(formData.username, formData.email, formData.password);
      showSuccess('Conta criada! Enviamos um link de confirmação para o seu email.');
      // Página dedicada de "confirme seu email" (com aviso de spam e reenvio)
      navigate('/confirm-email', {
        state: { email: data?.email || formData.email },
      });
    } catch (err) {
      showError(err.response?.data?.error || 'Erro ao criar conta. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      active="register"
      title={
        <>
          Seu VIP no <span className="sp-gradient-text">automático.</span>
        </>
      }
    >
      <h2 className="sp-card-title">Criar conta</h2>
      <p className="sp-card-sub">
        Leva menos de um minuto. Depois é só conectar seu bot e começar a vender.
      </p>

      <form onSubmit={handleSubmit}>
        {/* USERNAME */}
        <div className="sp-field">
          <label htmlFor="username" className="sp-label">Nome de usuário</label>
          <div className="sp-input-wrapper">
            <User className="sp-input-icon" size={16} aria-hidden="true" />
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              placeholder="seunome"
              required
              autoFocus
              autoComplete="username"
              disabled={loading}
              className="sp-input"
            />
          </div>
        </div>

        {/* EMAIL */}
        <div className="sp-field">
          <label htmlFor="email" className="sp-label">Email</label>
          <div className="sp-input-wrapper">
            <Mail className="sp-input-icon" size={16} aria-hidden="true" />
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="seu@email.com"
              required
              autoComplete="email"
              disabled={loading}
              className="sp-input"
            />
          </div>
        </div>

        {/* SENHA + CONFIRMAÇÃO lado a lado (cabe sem rolar) */}
        <div className="sp-field-pair">
          <div className="sp-field">
            <label htmlFor="password" className="sp-label">Senha</label>
            <div className="sp-input-wrapper">
              <Lock className="sp-input-icon" size={16} aria-hidden="true" />
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                minLength={8}
                placeholder="••••••••"
                autoComplete="new-password"
                disabled={loading}
                className="sp-input"
                style={{ paddingRight: '3rem' }}
              />
              <button
                type="button"
                className="sp-eye-toggle"
                aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                onClick={() => setShowPassword(!showPassword)}
                disabled={loading}
              >
                {showPassword ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
              </button>
            </div>
          </div>

          <div className="sp-field">
            <label htmlFor="confirmPassword" className="sp-label">Confirmar</label>
            <div className="sp-input-wrapper">
              <ShieldCheck className="sp-input-icon" size={16} aria-hidden="true" />
              <input
                type={showPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                minLength={8}
                placeholder="••••••••"
                autoComplete="new-password"
                disabled={loading}
                className="sp-input"
              />
            </div>
          </div>
        </div>

        {/* Força da senha numa linha: o que ainda falta */}
        <div className={`sp-strength ${formData.password ? 'show' : ''}`} aria-live="polite">
          <div className="sp-strength-bar">
            <div
              className="sp-strength-fill"
              style={{
                width: `${(score / CRITERIA.length) * 100}%`,
                background: strength.color,
              }}
            ></div>
          </div>
          <div className="sp-strength-text">
            <span style={{ color: strength.color }}>Senha {strength.label.toLowerCase()}</span>
            {missing.length > 0 && (
              <span className="sp-strength-missing"> · falta: {missing.map((c) => c.label.toLowerCase()).join(', ')}</span>
            )}
          </div>
        </div>

        {formData.confirmPassword.length > 0 && (
          <div className={`sp-match-hint ${passwordsMatch ? 'ok' : 'bad'}`}>
            {passwordsMatch ? <CircleCheck size={14} aria-hidden="true" /> : <CircleX size={14} aria-hidden="true" />}
            {passwordsMatch ? 'As senhas coincidem' : 'As senhas não coincidem'}
          </div>
        )}

        {/* BOTÃO */}
        <button type="submit" className="sp-btn-primary" disabled={loading}>
          {loading ? (
            <>
              <LoaderCircle className="sp-spin" size={18} aria-hidden="true" />
              Criando conta...
            </>
          ) : (
            <>
              Criar conta grátis
              <ArrowRight className="sp-btn-arrow" size={18} aria-hidden="true" />
            </>
          )}
        </button>

        <p className="sp-terms-note">
          Ao criar sua conta, você concorda com os{' '}
          <Link to="/legal/tos" className="sp-link">Termos de Uso</Link> da Spectrum.
        </p>
      </form>

      <div className="sp-card-footer">
        Já tem uma conta?{' '}
        <Link to="/login" className="sp-link">
          Fazer login
        </Link>
      </div>
    </AuthShell>
  );
}

export default Register;
